import { useEffect, useState } from 'react';
import type { AiGeneratedCard } from '@yorukiri/shared';
import { useTranslation } from '@yorukiri/i18n';
import { Button, Checkbox, Pill } from '@yorukiri/ui';
import { colors } from '@yorukiri/ui/tokens';
import { Sparkles } from './icons';
import { mockUser } from '../data/mock';

const sampleCards: AiGeneratedCard[] = [
  {
    type: 'cloze',
    front: 'The neurotransmitter {{c1::acetylcholine}} binds to muscarinic and nicotinic receptors.',
    back: 'acetylcholine',
  },
  {
    type: 'basic',
    front: 'Which enzyme degrades acetylcholine in the synaptic cleft?',
    back: 'Acetylcholinesterase (AChE).',
  },
  {
    type: 'mcq',
    front: 'Which receptor class do beta-blockers primarily antagonize to reduce heart rate?',
    back: 'β1-adrenergic',
    options: ['α1-adrenergic', 'β1-adrenergic', 'β2-adrenergic', 'M2 muscarinic'],
    correct: [1],
    explanation: 'β1-adrenergic receptors dominate cardiac chronotropy; blocking them lowers HR.',
  },
  {
    type: 'basic',
    front: 'Name two non-selective beta-blockers.',
    back: 'Propranolol and nadolol.',
  },
];

export type GenerationContext = {
  source: string;
  scope: 'selection' | 'page';
  suggestedName?: string;
};

type Props = {
  open: boolean;
  context: GenerationContext | null;
  onClose: () => void;
  onCreate?: (pageName: string, cards: AiGeneratedCard[]) => void;
};

function suggestName(ctx: GenerationContext, fallback: string): string {
  if (ctx.suggestedName) return ctx.suggestedName;
  if (ctx.scope === 'selection') {
    const first = ctx.source.trim().split(/\s+/).slice(0, 6).join(' ');
    return first ? first.replace(/[.,;:!?]$/, '') : fallback;
  }
  return fallback;
}

export function CardGenerationDialog({ open, context, onClose, onCreate }: Props) {
  const { t } = useTranslation('ai');
  const { t: tCommon } = useTranslation('common');
  const [pageName, setPageName] = useState('');
  const [loading, setLoading] = useState(false);
  const [cards, setCards] = useState<AiGeneratedCard[]>([]);
  const [accepted, setAccepted] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (!open || !context) return;
    setPageName(suggestName(context, t('placeholder_new_page')));
    setCards([]);
    setAccepted(new Set());
    setLoading(true);
    const timer = setTimeout(() => {
      setCards(sampleCards);
      setAccepted(new Set(sampleCards.map((_, i) => i)));
      setLoading(false);
    }, 850);
    return () => clearTimeout(timer);
  }, [open, context, t]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !context) return null;

  const toggle = (i: number) =>
    setAccepted((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  const create = () => {
    const name = pageName.trim() || 'Untitled page';
    onCreate?.(name, cards.filter((_, i) => accepted.has(i)));
    onClose();
  };

  const acceptedCount = accepted.size;
  const canCreate = !loading && acceptedCount > 0 && pageName.trim().length > 0;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(17,17,20,0.4)',
        backdropFilter: 'blur(4px)',
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: colors.surface,
          borderRadius: 14,
          border: `1px solid ${colors.line2}`,
          boxShadow: '0 30px 60px -20px rgba(17,17,20,0.3), 0 10px 20px -10px rgba(17,17,20,0.15)',
          width: '100%',
          maxWidth: 720,
          maxHeight: '86vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Header scope={context.scope} onClose={onClose} />
        <SourcePreview text={context.source} />

        <div
          style={{
            padding: '0 24px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginTop: 4,
            marginBottom: 12,
          }}
        >
          <span
            style={{
              fontVariantNumeric: 'tabular-nums',
              fontSize: 10,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: colors.ink3,
            }}
          >
            {t('new_page_label')}
          </span>
          <input
            value={pageName}
            onChange={(e) => setPageName(e.target.value)}
            placeholder={t('placeholder_new_page')}
            style={{
              flex: 1,
              background: colors.surface2,
              border: `1px solid ${colors.line2}`,
              borderRadius: 6,
              padding: '6px 10px',
              color: colors.ink,
              fontFamily: 'inherit',
              fontSize: 13,
              fontWeight: 500,
              outline: 'none',
            }}
          />
        </div>

        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '0 24px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {loading ? (
            <Skeleton />
          ) : (
            cards.map((c, i) => (
              <CardRow
                key={i}
                index={i + 1}
                card={c}
                accepted={accepted.has(i)}
                onToggle={() => toggle(i)}
              />
            ))
          )}
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '14px 24px',
            borderTop: `1px solid ${colors.line}`,
            background: colors.surface2,
          }}
        >
          <span style={{ fontSize: 12, color: colors.ink3 }}>
            {loading
              ? t('generating')
              : t('selected_count', {
                  accepted: acceptedCount,
                  total: cards.length,
                  credits: mockUser.credits.toLocaleString(),
                })}
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <Button variant="ghost" onClick={onClose}>
              {tCommon('cancel')}
            </Button>
            <Button onClick={create} disabled={!canCreate} style={{ gap: 8 }}>
              <Sparkles size={13} />
              {acceptedCount > 0
                ? t('create_page_with_cards', { count: acceptedCount })
                : t('create_page_button')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Header({ scope, onClose }: { scope: GenerationContext['scope']; onClose: () => void }) {
  const { t } = useTranslation('ai');
  return (
    <div
      style={{
        padding: '18px 24px 14px',
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        borderBottom: `1px solid ${colors.line}`,
      }}
    >
      <div>
        <div
          style={{
            fontVariantNumeric: 'tabular-nums',
            fontSize: 10,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: colors.accent,
            marginBottom: 4,
          }}
        >
          {scope === 'selection' ? t('scope_selection') : t('scope_page')}
        </div>
        <h3
          style={{
            fontWeight: 600,
            letterSpacing: '-0.015em',
            fontSize: 22,
            margin: 0,
            display: 'flex',
            alignItems: 'baseline',
            gap: 8,
          }}
        >
          {t('dialog_title')}{' '}
          <em style={{ fontStyle: 'italic', color: colors.ink3 }}>{t('dialog_title_em')}</em>
        </h3>
      </div>
      <button
        onClick={onClose}
        style={{
          background: 'transparent',
          border: 'none',
          color: colors.ink3,
          fontSize: 18,
          lineHeight: 1,
          cursor: 'pointer',
          padding: 4,
          fontFamily: 'inherit',
        }}
      >
        ×
      </button>
    </div>
  );
}

function SourcePreview({ text }: { text: string }) {
  const short = text.length > 400 ? `${text.slice(0, 400)}…` : text;
  return (
    <div style={{ padding: '12px 24px 0' }}>
      <div
        style={{
          background: colors.surface2,
          border: `1px solid ${colors.line}`,
          borderRadius: 8,
          padding: '10px 12px',
          fontSize: 12.5,
          color: colors.ink2,
          lineHeight: 1.5,
          maxHeight: 96,
          overflowY: 'auto',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {short || <em style={{ color: colors.ink3 }}>(empty)</em>}
      </div>
    </div>
  );
}

function CardRow({
  index,
  card,
  accepted,
  onToggle,
}: {
  index: number;
  card: AiGeneratedCard;
  accepted: boolean;
  onToggle: () => void;
}) {
  return (
    <div
      style={{
        background: colors.surface,
        border: `1px solid ${accepted ? colors.line2 : colors.line}`,
        borderRadius: 9,
        padding: '11px 14px',
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto',
        gap: 12,
        alignItems: 'start',
        opacity: accepted ? 1 : 0.55,
      }}
    >
      <span
        style={{
          fontVariantNumeric: 'tabular-nums',
          fontSize: 10,
          color: colors.ink3,
          letterSpacing: '0.08em',
          paddingTop: 2,
        }}
      >
        {String(index).padStart(2, '0')}
      </span>
      <div>
        <Pill
          tone={card.type === 'cloze' ? 'warn' : card.type === 'mcq' ? 'good' : 'accent'}
          size="sm"
          style={{ marginBottom: 4 }}
        >
          {card.type}
        </Pill>
        <div style={{ fontSize: 13.5, color: colors.ink, lineHeight: 1.5, marginBottom: 4 }}>
          {card.type === 'cloze' ? renderCloze(card.front) : card.front}
        </div>
        {card.type === 'basic' && (
          <div style={{ fontSize: 12.5, color: colors.ink3, fontStyle: 'italic' }}>{card.back}</div>
        )}
        {card.type === 'mcq' && <McqPreview options={card.options} correct={card.correct} />}
      </div>
      <Checkbox checked={accepted} onChange={onToggle} />
    </div>
  );
}

function McqPreview({ options, correct }: { options: string[]; correct: number[] }) {
  const correctSet = new Set(correct);
  return (
    <ul style={{ margin: '4px 0 0', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 3 }}>
      {options.map((option, i) => {
        const isCorrect = correctSet.has(i);
        return (
          <li
            key={i}
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 8,
              fontSize: 12.5,
              color: isCorrect ? colors.good : colors.ink3,
              fontWeight: isCorrect ? 500 : 400,
            }}
          >
            <span
              style={{
                fontVariantNumeric: 'tabular-nums',
                fontSize: 10,
                letterSpacing: '0.08em',
                minWidth: 14,
              }}
            >
              {isCorrect ? '✓' : String.fromCharCode(65 + i)}
            </span>
            <span>{option}</span>
          </li>
        );
      })}
    </ul>
  );
}

function renderCloze(text: string) {
  const parts = text.split(/(\{\{c\d+::[^}]+\}\})/g);
  return parts.map((p, i) => {
    const m = p.match(/^\{\{c(\d+)::([^}]+)\}\}$/);
    if (!m) return <span key={i}>{p}</span>;
    return (
      <span
        key={i}
        style={{
          background: 'rgba(176, 138, 42, 0.26)',
          padding: '0 2px',
          borderRadius: 3,
          fontWeight: 500,
        }}
      >
        {m[2]}
      </span>
    );
  });
}

function Skeleton() {
  return (
    <>
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          style={{
            height: 64,
            background: colors.surface,
            border: `1px solid ${colors.line}`,
            borderRadius: 9,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: `linear-gradient(90deg, transparent, ${colors.line2}, transparent)`,
              animation: `shimmer 1.4s ${i * 0.15}s infinite`,
            }}
          />
        </div>
      ))}
      <style>{`@keyframes shimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }`}</style>
    </>
  );
}
